import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleDollarSign, Download } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@veorest/ui";
import {
  downloadCsv,
  exportAdminReportCsv,
  fetchAdminReports,
} from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { useAdminFeatureFlags } from "../lib/useAdminFeatureFlags";
import { StatsStrip } from "../components/data/StatsStrip";
import { formatDate, formatIqd, formatNumber } from "../lib/format";

export default function ReportsPage() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const flags = useAdminFeatureFlags();

  const reports = useQuery({
    queryKey: ["admin-reports", from, to],
    queryFn: () => fetchAdminReports({ from: from || undefined, to: to || undefined }),
  });

  const exportKind = async (kind: "payments" | "credit_usage" | "orders") => {
    try {
      const csv = await exportAdminReportCsv(kind);
      downloadCsv(`oelox-${kind}.csv`, csv);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    }
  };

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Analytics"
        title="Reports"
        subtitle="Revenue, usage, credit consumption, and top customers."
        actions={
          flags.reportsExport ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              className="min-h-[40px]"
              icon={<Download className="h-4 w-4" />}
              onClick={() => void exportKind("payments")}
            >
              Payments
            </Button>
            <Button
              variant="secondary"
              className="min-h-[40px]"
              icon={<Download className="h-4 w-4" />}
              onClick={() => void exportKind("credit_usage")}
            >
              Credits
            </Button>
            <Button
              variant="secondary"
              className="min-h-[40px]"
              icon={<Download className="h-4 w-4" />}
              onClick={() => void exportKind("orders")}
            >
              Orders
            </Button>
          </div>
          ) : undefined
        }
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <label className="text-sm font-semibold text-[var(--text-secondary)]">
          From
          <input
            type="date"
            className="input-base mt-1"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="text-sm font-semibold text-[var(--text-secondary)]">
          To
          <input
            type="date"
            className="input-base mt-1"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
      </div>

      <StatsStrip
        columns={3}
        loading={reports.isLoading && !reports.data}
        tiles={[
          {
            key: "revenue",
            label: "Credit revenue",
            value: formatIqd(reports.data?.revenue.totalIqd ?? 0),
            icon: CircleDollarSign,
            tone: "success",
            caption: `${formatNumber(reports.data?.revenue.paymentCount ?? 0)} paid payments`,
          },
        ]}
      />

      <div className="overview-page__grid mt-4">
        <section className="overview-panel">
          <header className="overview-panel__header">
            <div className="min-w-0 flex-1">
              <h3 className="overview-panel__title">Orders usage by client</h3>
              <p className="overview-panel__subtitle">Billable order volume and GMV</p>
            </div>
          </header>
          <div className="overview-panel__body">
            <div className="admin-table-wrap max-h-80 overflow-auto">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Orders</th>
                    <th>GMV</th>
                  </tr>
                </thead>
                <tbody>
                  {(reports.data?.usageByRestaurant ?? []).map((r) => (
                    <tr key={r.id}>
                      <td className="font-bold">{r.name}</td>
                      <td className="tabular">{formatNumber(r.orderCount)}</td>
                      <td className="tabular">{formatNumber(r.gmv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="overview-panel">
          <header className="overview-panel__header">
            <div className="min-w-0 flex-1">
              <h3 className="overview-panel__title">Credit consumption</h3>
              <p className="overview-panel__subtitle">Order deductions by client</p>
            </div>
          </header>
          <div className="overview-panel__body">
            <div className="admin-table-wrap max-h-80 overflow-auto">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Consumed</th>
                  </tr>
                </thead>
                <tbody>
                  {(reports.data?.creditConsumption ?? []).map((r) => (
                    <tr key={r.id}>
                      <td className="font-bold">{r.name}</td>
                      <td className="admin-money">{formatIqd(r.consumed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <section className="overview-panel mt-4">
        <header className="overview-panel__header">
          <div className="min-w-0 flex-1">
            <h3 className="overview-panel__title">Top customers</h3>
            <p className="overview-panel__subtitle">
              Highest spend across client CRM Â· {formatDate(new Date().toISOString())}
            </p>
          </div>
        </header>
        <div className="overview-panel__body">
          <div className="admin-table-wrap max-h-80 overflow-auto">
            <table className="table-flat">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Client</th>
                  <th>Orders</th>
                  <th>Spent</th>
                </tr>
              </thead>
              <tbody>
                {(reports.data?.topCustomers ?? []).map((c) => (
                  <tr key={String(c.id)}>
                    <td>
                      <div className="font-bold">{String(c.name)}</div>
                      <div className="text-xs text-[var(--text-muted)]">{String(c.phone || "")}</div>
                    </td>
                    <td>{String(c.restaurant_name)}</td>
                    <td className="tabular">{formatNumber(Number(c.total_orders))}</td>
                    <td className="tabular">{formatNumber(Number(c.total_spent))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

